import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, ShieldCheck, Sun, Star } from 'lucide-react';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      title: "Metal Dhan Yog Bracelet & Pure Ghee",
      subtitle: "Himalayan Sacred Collection",
      rating: "4.8",
      image: "https://images.unsplash.com/photo-1692107089367-791b734a426c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
    },
    {
      title: "Original Kedarnath Rudraksha & Prasad",
      subtitle: "Sanctified Char Dham Shrines",
      rating: "4.9",
      image: "https://images.unsplash.com/photo-1649147313351-c86537fda0eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
    },
    {
      title: "A2 Badri Cow Pure Bilona Ghee",
      subtitle: "Hand-Churned in Badrinath",
      rating: "5.0",
      image: "https://static.prod-images.emergentagent.com/jobs/3b22d7d5-5c6a-452b-b567-97eedc4bf054/images/1e9258f81e6dd024966d2af7ca9b7f828758d65dc5938a231063b2f9a415a60a.png"
    },
    {
      title: "Copper Shree Yantra & Sacred Mala",
      subtitle: "Blessed for Abundance & Prosperity",
      rating: "4.9",
      image: "https://images.unsplash.com/photo-1622993361118-b6365d859ab6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
    }
  ];

  // Auto scroll every 3.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  return (
    <section className="relative min-h-[calc(100vh-88px)] flex items-center justify-center py-6 sm:py-10 lg:py-6 px-4 sm:px-6 lg:px-12 bg-[#FBF4E7] overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-[#E8D5A8]/30 rounded-full blur-3xl pointer-events-none -translate-y-1/2" />
      <div className="absolute bottom-10 right-10 w-[30rem] h-[30rem] bg-[#6E1B34]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center relative z-10">
        
        {/* LEFT COLUMN: Content */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 space-y-4 lg:space-y-5 text-left"
        >
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#F3E7D0] border border-[#E7D9BC]"
          >
            <Sun className="w-3.5 h-3.5 text-[#D9711F]" />
            <span className="text-[11px] sm:text-xs font-bold tracking-widest text-[#B85A15] uppercase">
              BRING DIVINITY INTO YOUR EVERYDAY
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-serif-heading text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#4E1225] leading-[1.16] tracking-tight"
          >
            Keep the Divine Close to Your <span className="text-[#6E1B34]">Heart & Home</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="text-sm sm:text-base text-[#83725B] max-w-xl font-normal leading-relaxed"
          >
            Discover thoughtfully selected divine products that bring peace, positivity and a deeper sense of connection into your everyday surroundings.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2.5 sm:space-y-0 sm:space-x-3.5 pt-1"
          >
            <Link to="/store">
              <motion.button
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto px-6 py-3.5 rounded-md bg-[#6E1B34] text-[#FFFFFF] font-bold text-sm sm:text-base shadow-md hover:bg-[#4E1225] transition-all flex items-center justify-center space-x-2.5"
              >
                <span>Explore Divine Products</span>
                <ArrowRight className="w-4 h-4 text-[#E8D5A8]" />
              </motion.button>
            </Link>

            <Link to="/store">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto px-6 py-3.5 rounded-md bg-transparent border-2 border-[#6E1B34] text-[#6E1B34] font-semibold text-sm sm:text-base hover:bg-[#6E1B34] hover:text-[#FFFFFF] transition-colors flex items-center justify-center"
              >
                Discover Our Story
              </motion.button>
            </Link>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="pt-4 border-t border-[#E7D9BC] flex flex-wrap gap-2.5"
          >
            <span className="px-3 py-1 bg-white border border-[#E7D9BC] rounded-full text-[11px] sm:text-xs font-semibold text-[#2A1F16] flex items-center gap-1.5 shadow-2xs">
              <ShieldCheck className="w-3.5 h-3.5 text-[#2E7D45]" /> 100% Authentic
            </span>
            <span className="px-3 py-1 bg-white border border-[#E7D9BC] rounded-full text-[11px] sm:text-xs font-semibold text-[#2A1F16] flex items-center gap-1.5 shadow-2xs">
              <Sun className="w-3.5 h-3.5 text-[#D9711F]" /> Energised by Pandits
            </span>
            <span className="px-3 py-1 bg-white border border-[#E7D9BC] rounded-full text-[11px] sm:text-xs font-semibold text-[#2A1F16] flex items-center gap-1.5 shadow-2xs">
              <Sparkles className="w-3.5 h-3.5 text-[#6E1B34]" /> Pan-India Delivery
            </span>
          </motion.div>

        </motion.div>

        {/* RIGHT COLUMN: Large Prominent Auto-Sliding Visual Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6 relative flex flex-col items-center justify-center"
        >
          {/* Image Frame */}
          <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl aspect-[4/4.5] min-h-[380px] lg:min-h-[450px] rounded-3xl overflow-hidden shadow-xl border-4 border-[#F3E7D0]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                <img
                  src={heroSlides[currentSlide].image}
                  alt={heroSlides[currentSlide].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4E1225]/80 via-transparent to-transparent" />
              </motion.div>
            </AnimatePresence>

            {/* Slide Info Overlay */}
            <div className="absolute bottom-5 left-5 right-5 p-4 rounded-2xl bg-[#FBF4E7]/95 backdrop-blur-md border border-[#B98B3E] shadow-md flex items-center justify-between z-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                >
                  <p className="text-[11px] font-bold text-[#D9711F] uppercase tracking-wider">
                    {heroSlides[currentSlide].subtitle}
                  </p>
                  <p className="font-serif-heading text-base sm:text-lg font-bold text-[#4E1225] line-clamp-1">
                    {heroSlides[currentSlide].title}
                  </p>
                </motion.div>
              </AnimatePresence>

              <span className="px-3 py-1 bg-[#6E1B34] text-[#E8D5A8] text-xs font-bold rounded-md flex items-center gap-1 flex-shrink-0">
                <Star className="w-3.5 h-3.5 fill-current text-[#D9711F]" /> {heroSlides[currentSlide].rating}
              </span>
            </div>
          </div>

          {/* Auto Slider Indicators / Dots */}
          <div className="flex items-center space-x-2.5 mt-4 z-10">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  currentSlide === index
                    ? 'w-8 h-2.5 bg-[#6E1B34]'
                    : 'w-2.5 h-2.5 bg-[#E7D9BC] hover:bg-[#B98B3E]'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

        </motion.div>

      </div>
    </section>
  );
};

export default HeroSection;
